// Platform-agnostic sync cycle: Merkle diff → fetch ranges → write locally → tail.
// All platform dependencies (fetch, crypto.subtle) are Web standard APIs.
// Deno.* never appears here.

import type { ISyncBackend } from "../storage/backend.ts";
import type { EventEntry, MerkleMismatch, ObjectEntry } from "../storage/capabilities.ts";
import { buildEventMerkleTree, buildObjectMerkleTree, type IMerkleTree } from "./diff.ts";
import {
  DEFAULT_FETCH_PAGE_SIZE,
  TAIL_DURATION_MS,
  MERKLE_DIFF_LEVELS_PER_ROUND,
  MERKLE_TREE_END,
  MERKLE_LEAF_SIZE,
} from "../commons/constants.ts";
import { STATUS_NO_CONTENT } from "../api/commons/statuses.ts";

// Discriminated change event emitted by watch() and returned by sync functions.
export type ChangeEvent =
  | { type: "upsert"; topic: string; entry: EventEntry | ObjectEntry }
  | { type: "delete"; topic: string; id: string };

type DiffRoundResponse =
  | { kind: "match" }
  | { kind: "diff"; mismatches: { start: number; end: number; isLeaf: boolean }[] };

export type SyncProgressEvent =
  | { phase: "diff";  round: number }
  | { phase: "fetch"; count: number };

type MerkleNode = { start: number; end: number };
type HashedNode = MerkleNode & { hash: string };
type ProgressFn = (event: SyncProgressEvent) => void;

const MERKLE_ROOT: MerkleNode = { start: 0, end: MERKLE_TREE_END };

function authHeaders(token: string): Record<string, string> {
  return { "Authorization": `Bearer ${token}` };
}

async function postDiffRound(
  baseUrl: string,
  topic: string,
  token: string,
  nodes: HashedNode[],
): Promise<DiffRoundResponse> {
  const res = await fetch(`${baseUrl}/diff/${topic}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(token) },
    body: JSON.stringify({ nodes }),
  });
  if (res.status === STATUS_NO_CONTENT) return { kind: "match" };
  if (!res.ok) throw new Error(`POST /diff/${topic} failed: ${res.status}`);
  type DiffResponse = { mismatches: MerkleMismatch[] };
  const json = await res.json() as DiffResponse;
  return { kind: "diff", mismatches: json.mismatches };
}

// Returns the descendants of a node `levels` levels below it, stopping at leaf size.
// Sending a whole band of levels per request is what keeps the descent to a few round trips.
export function descendants(node: MerkleNode, levels: number): MerkleNode[] {
  let current: MerkleNode[] = [node];
  for (let level = 0; level < levels; level++) {
    const next: MerkleNode[] = [];
    for (const range of current) {
      if (range.end - range.start <= MERKLE_LEAF_SIZE) {
        next.push(range);
        continue;
      }
      const mid = Math.floor((range.start + range.end) / 2);
      next.push({ start: range.start, end: mid }, { start: mid, end: range.end });
    }
    if (next.length === current.length) return current;
    current = next;
  }
  return current;
}

// Hashes a band of nodes from the local tree. Trees cache node hashes, so siblings are cheap.
function hashNodes(tree: IMerkleTree, nodes: MerkleNode[]): Promise<HashedNode[]> {
  const hashNode = async (node: MerkleNode) => ({
    ...node,
    hash: await tree.hashForRange(node.start, node.end),
  });
  return Promise.all(nodes.map(hashNode));
}

// Posts the root hash alone. A match means local and remote agree, so no descent is needed.
// This is the whole cost of a sync cycle that finds nothing new.
export async function rootMatches(
  baseUrl: string,
  topic: string,
  token: string,
  tree: IMerkleTree,
  onProgress?: ProgressFn,
): Promise<boolean> {
  onProgress?.({ phase: "diff", round: 1 });
  const nodes = await hashNodes(tree, [MERKLE_ROOT]);
  const response = await postDiffRound(baseUrl, topic, token, nodes);
  return response.kind === "match";
}

// Descends to the mismatching leaves, expanding MERKLE_DIFF_LEVELS_PER_ROUND levels per
// request. Callers establish the root mismatch first with rootMatches.
export async function merkleDescent(
  baseUrl: string,
  topic: string,
  token: string,
  tree: IMerkleTree,
  onProgress?: ProgressFn,
): Promise<MerkleNode[]> {
  let frontier = await hashNodes(tree, descendants(MERKLE_ROOT, MERKLE_DIFF_LEVELS_PER_ROUND));
  const leafRanges: MerkleNode[] = [];
  let round = 1;

  while (frontier.length > 0) {
    round++;
    onProgress?.({ phase: "diff", round });
    const response = await postDiffRound(baseUrl, topic, token, frontier);
    if (response.kind === "match") break;

    const expanded: MerkleNode[] = [];
    for (const mismatch of response.mismatches) {
      if (mismatch.isLeaf) {
        leafRanges.push({ start: mismatch.start, end: mismatch.end });
      } else {
        expanded.push(...descendants(mismatch, MERKLE_DIFF_LEVELS_PER_ROUND));
      }
    }
    frontier = await hashNodes(tree, expanded);
  }

  return leafRanges;
}

async function fetchEventRange(
  baseUrl: string,
  topic: string,
  token: string,
  start: number,
  size: number,
): Promise<EventEntry[]> {
  const url = `${baseUrl}/events/${topic}?start=${start}&size=${size}`;
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`GET /events/${topic} failed: ${res.status}`);
  const body = await res.json() as { entries: EventEntry[] };
  return body.entries ?? [];
}

async function fetchObjectRange(
  baseUrl: string,
  topic: string,
  token: string,
  start: number,
  size: number,
): Promise<ObjectEntry[]> {
  const url = `${baseUrl}/objects/${topic}?start=${start}&size=${size}`;
  const res = await fetch(url, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(`GET /objects/${topic} failed: ${res.status}`);
  const body = await res.json() as { entries: ObjectEntry[] };
  return body.entries ?? [];
}

// Pages through remote events from startId, yielding each entry. Stops on the first short page.
async function* pageEvents(
  baseUrl: string,
  topic: string,
  token: string,
  startId: number,
): AsyncGenerator<EventEntry> {
  let start = startId;
  while (true) {
    const entries = await fetchEventRange(baseUrl, topic, token, start, DEFAULT_FETCH_PAGE_SIZE);
    for (const entry of entries) yield entry;
    if (entries.length < DEFAULT_FETCH_PAGE_SIZE) return;
    start = entries[entries.length - 1].id + 1;
  }
}

// Pages through remote objects from startSeq, yielding each entry. Stops on the first short page.
async function* pageObjects(
  baseUrl: string,
  topic: string,
  token: string,
  startSeq: number,
): AsyncGenerator<ObjectEntry> {
  let start = startSeq;
  while (true) {
    const entries = await fetchObjectRange(baseUrl, topic, token, start, DEFAULT_FETCH_PAGE_SIZE);
    for (const entry of entries) yield entry;
    if (entries.length < DEFAULT_FETCH_PAGE_SIZE) return;
    start = entries[entries.length - 1].seq + 1;
  }
}

// Tails the remote NDJSON stream briefly to catch writes during the diff round-trip.
// The remote stream never ends by itself, so a non-zero duration always costs its full
// window. Pass 0 to skip the tail and let the next poll pick those writes up instead.
async function tailEventStream(
  baseUrl: string,
  topic: string,
  token: string,
  startId: number,
  durationMs = TAIL_DURATION_MS,
): Promise<EventEntry[]> {
  if (durationMs <= 0) return [];

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), durationMs);
  const collected: EventEntry[] = [];
  try {
    const res = await fetch(`${baseUrl}/events/${topic}?start=${startId}`, {
      headers: { ...authHeaders(token), "Accept": "application/x-ndjson" },
      signal: controller.signal,
    });
    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";
      for (const line of lines) {
        if (line.trim()) collected.push(JSON.parse(line) as EventEntry);
      }
    }
  } catch (err) {
    if (!(err instanceof DOMException && err.name === "AbortError")) throw err;
  } finally {
    clearTimeout(timeout);
  }
  return collected;
}

// Applies a remote event entry locally and returns a ChangeEvent for emission.
// skipMerkle skips cache invalidation — used during first sync where no cache exists yet.
async function applyEventEntry(
  backend: ISyncBackend,
  topic: string,
  entry: EventEntry,
  skipMerkle = false,
): Promise<ChangeEvent> {
  const timestamps = { createdAt: entry.createdAt, updatedAt: entry.updatedAt };
  await backend.events.updateEvent(topic, entry.id, entry.payload, timestamps);
  if (!skipMerkle) await backend.merkleEvents?.invalidatePath(topic, entry.id);
  return { type: "upsert", topic, entry };
}

// Applies a remote object entry locally (routes tombstones to deleteObject).
// Remote seq and timestamps are preserved so local diff hashes match the server's.
// skipMerkle skips cache invalidation — used during first sync where no cache exists yet.
async function applyObjectEntry(
  backend: ISyncBackend,
  topic: string,
  entry: ObjectEntry,
  skipMerkle = false,
): Promise<ChangeEvent> {
  const readCached = (!skipMerkle && backend.merkleObjects);
  const existing = readCached ? await backend.objects.readObject(topic, entry.id) : undefined;
  const oldSeq = existing?.seq;
  const timestamps = {
    seq: entry.seq,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
  };
  if (entry.payload === null) {
    await backend.objects.deleteObject(topic, entry.id, timestamps);
  } else {
    await backend.objects.upsertObject(topic, entry.id, entry.payload, timestamps);
  }
  if (!skipMerkle) await backend.merkleObjects?.invalidatePaths(topic, entry.seq, oldSeq);
  if (entry.payload === null) return { type: "delete", topic, id: entry.id };
  return { type: "upsert", topic, entry };
}

// Returns the local Merkle tree for an event topic. Uses the backend's persistent hash cache
// when it has one; otherwise builds a throwaway tree from all local entries.
function eventTree(backend: ISyncBackend, topic: string): Promise<IMerkleTree> {
  if (backend.merkleEvents) return Promise.resolve(backend.merkleEvents.forTopic(topic));
  return backend.events.readEvents(topic, {})
    .then(entries => buildEventMerkleTree(entries ?? []));
}

// Returns the local Merkle tree for an object topic, over the seq dimension.
function objectTree(backend: ISyncBackend, topic: string): Promise<IMerkleTree> {
  if (backend.merkleObjects) return Promise.resolve(backend.merkleObjects.forTopic(topic));
  return backend.objects.readObjectsBySeq(topic, {})
    .then(entries => buildObjectMerkleTree(entries ?? []));
}

// Runs one sync cycle for an event topic: Merkle diff → fetch ranges → tail → write locally.
// Returns ChangeEvents for every entry written so the caller can emit them to watchers.
export async function syncEventTopic(
  backend: ISyncBackend,
  baseUrl: string,
  token: string,
  topic: string,
  tailDurationMs?: number,
  onProgress?: ProgressFn,
): Promise<ChangeEvent[]> {
  const cursor = await backend.cursors.getEventCursor(topic);
  const changes: ChangeEvent[] = [];
  let maxId = cursor;

  if (cursor === 0) {
    // Full fetch on first sync — avoids Merkle traversal overhead when local state is empty.
    // skipMerkle=true: no cache exists yet so invalidation is pure overhead.
    for await (const entry of pageEvents(baseUrl, topic, token, 1)) {
      changes.push(await applyEventEntry(backend, topic, entry, true));
      maxId = Math.max(maxId, entry.id);
      onProgress?.({ phase: "fetch", count: changes.length });
    }
    // Tail catches writes that arrived on the remote during the bulk fetch window
    const tailed = await tailEventStream(baseUrl, topic, token, maxId + 1, tailDurationMs);
    for (const entry of tailed) {
      changes.push(await applyEventEntry(backend, topic, entry, true));
      maxId = Math.max(maxId, entry.id);
    }
    if (maxId > 0) await backend.cursors.setEventCursor(topic, maxId);
    return changes;
  }

  const tree = await eventTree(backend, topic);
  if (await rootMatches(baseUrl, topic, token, tree, onProgress)) return changes;

  // Event topics are append-only, so divergence is almost always entries past the cursor.
  // One paged fetch settles that case and leaves the descent nothing to find.
  for await (const entry of pageEvents(baseUrl, topic, token, cursor + 1)) {
    changes.push(await applyEventEntry(backend, topic, entry));
    maxId = Math.max(maxId, entry.id);
    onProgress?.({ phase: "fetch", count: changes.length });
  }

  // The tree must be rebuilt before re-checking: a throwaway tree holds a stale snapshot.
  const appended = changes.length > 0;
  const refreshed = appended ? await eventTree(backend, topic) : tree;
  const settled = appended &&
    await rootMatches(baseUrl, topic, token, refreshed, onProgress);

  if (!settled) {
    // Remaining divergence is an in-place edit, so fall back to the full descent.
    const leafRanges = await merkleDescent(baseUrl, topic, token, refreshed, onProgress);
    for (const range of leafRanges) {
      // range.start is the exclusive lower bound; fetch from start+1
      const size = MERKLE_LEAF_SIZE;
      const entries = await fetchEventRange(baseUrl, topic, token, range.start + 1, size);
      for (const entry of entries) {
        changes.push(await applyEventEntry(backend, topic, entry));
        maxId = Math.max(maxId, entry.id);
        onProgress?.({ phase: "fetch", count: changes.length });
      }
    }
  }

  const tailed = await tailEventStream(baseUrl, topic, token, maxId + 1, tailDurationMs);
  for (const entry of tailed) {
    changes.push(await applyEventEntry(backend, topic, entry));
    maxId = Math.max(maxId, entry.id);
  }

  if (maxId > 0) await backend.cursors.setEventCursor(topic, maxId);
  return changes;
}

// Runs one sync cycle for an object topic: Merkle diff → fetch ranges → write locally.
// Returns ChangeEvents for every entry written so the caller can emit them to watchers.
export async function syncObjectTopic(
  backend: ISyncBackend,
  baseUrl: string,
  token: string,
  topic: string,
  onProgress?: ProgressFn,
): Promise<ChangeEvent[]> {
  const cursor = await backend.cursors.getObjectCursor(topic);
  const changes: ChangeEvent[] = [];
  let maxSeq = cursor;

  if (cursor === 0) {
    // Full fetch on first sync — avoids Merkle traversal overhead when local state is empty.
    // skipMerkle=true: no cache exists yet so invalidation is pure overhead.
    for await (const entry of pageObjects(baseUrl, topic, token, 1)) {
      changes.push(await applyObjectEntry(backend, topic, entry, true));
      maxSeq = Math.max(maxSeq, entry.seq);
      onProgress?.({ phase: "fetch", count: changes.length });
    }
    if (maxSeq > 0) await backend.cursors.setObjectCursor(topic, maxSeq);
    return changes;
  }

  const tree = await objectTree(backend, topic);
  if (await rootMatches(baseUrl, topic, token, tree, onProgress)) return changes;

  // Every upsert and tombstone takes a fresh, higher seq, so all changes sit past the cursor.
  // One paged fetch settles that case and leaves the descent nothing to find.
  for await (const entry of pageObjects(baseUrl, topic, token, cursor + 1)) {
    changes.push(await applyObjectEntry(backend, topic, entry));
    maxSeq = Math.max(maxSeq, entry.seq);
    onProgress?.({ phase: "fetch", count: changes.length });
  }

  // The tree must be rebuilt before re-checking: a throwaway tree holds a stale snapshot.
  const appended = changes.length > 0;
  const refreshed = appended ? await objectTree(backend, topic) : tree;
  const settled = appended &&
    await rootMatches(baseUrl, topic, token, refreshed, onProgress);

  if (!settled) {
    const leafRanges = await merkleDescent(baseUrl, topic, token, refreshed, onProgress);
    for (const range of leafRanges) {
      // range.start is the exclusive lower bound; fetch from start+1
      const size = MERKLE_LEAF_SIZE;
      const entries = await fetchObjectRange(baseUrl, topic, token, range.start + 1, size);
      for (const entry of entries) {
        changes.push(await applyObjectEntry(backend, topic, entry));
        maxSeq = Math.max(maxSeq, entry.seq);
        onProgress?.({ phase: "fetch", count: changes.length });
      }
    }
  }

  if (maxSeq > 0) await backend.cursors.setObjectCursor(topic, maxSeq);
  return changes;
}
